import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AssetsService, GeographyOption } from '../../../core/services/assets.service';

@Component({
  selector: 'app-city-select, app-city-autocomplete-input',
  standalone: false,
  templateUrl: './city-select.html',
  styleUrl: './city-select.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CitySelect), multi: true }],
})
export class CitySelect implements ControlValueAccessor, OnChanges {
  /** ISO-2 or ISO-3 country code. `countryId`/`stateId` remain temporary aliases. */
  @Input() countryIso = '';
  @Input() stateIso = '';
  @Input() countryId = '';
  @Input() stateId = '';
  @Input() placeholder = 'Search city';
  @Input() selectClass = 'form-select';
  @Output() cityChange = new EventEmitter<GeographyOption | null>();
  @Output() availabilityChange = new EventEmitter<boolean>();

  readonly control = new FormControl('', { nonNullable: true });
  readonly search = (query: string) => this.load(query);
  readonly preload = (values: string[]) => Promise.all(values.filter(Boolean).map((value) => this.find(value)));
  options: GeographyOption[] = [];
  private savedValue = '';
  private onChange = (_value: string) => {};
  private onTouched = () => {};
  private disabled = false;
  private loadSequence = 0;

  constructor(private readonly assets: AssetsService) {
    this.control.valueChanges.subscribe((value) => this.select(value));
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes['countryIso'] && !changes['stateIso'] && !changes['countryId'] && !changes['stateId']) return;
    const country = this.country();
    const state = this.state();
    const sequence = ++this.loadSequence;
    this.options = [];
    if (!this.contextReady()) {
      this.control.setValue('', { emitEvent: false });
      this.availabilityChange.emit(false);
      this.syncDisabled();
      return;
    }
    const options = await this.assets.getCities(country, state);
    if (sequence !== this.loadSequence || country !== this.country() || state !== this.state()) return;
    this.options = options;
    if (this.savedValue && !this.options.some((option) => option.value === this.savedValue)) {
      this.control.setValue('', { emitEvent: false });
    }
    this.availabilityChange.emit(this.options.length > 0);
    this.syncDisabled();
  }

  writeValue(value: string | null): void {
    this.savedValue = String(value || '');
    this.control.setValue(this.savedValue, { emitEvent: false });
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; this.syncDisabled(); }
  touched(): void { this.onTouched(); }

  private async load(query: string): Promise<GeographyOption[]> {
    if (!this.contextReady()) return [];
    const country = this.country();
    const state = this.state();
    const sequence = this.loadSequence;
    const options = await this.assets.getCities(country, state, query);
    if (sequence !== this.loadSequence || country !== this.country() || state !== this.state()) return [];
    this.options = mergeOptions(this.options, options);
    return options;
  }

  private async find(value: string): Promise<GeographyOption> {
    const selected = this.options.find((option) => option.value === value);
    if (selected) return selected;
    await this.load('');
    const loaded = this.options.find((option) => option.value === value);
    if (!loaded) throw new Error(`City '${value}' was not found for the selected state.`);
    return loaded;
  }

  private select(value: string): void {
    const selected = this.options.find((option) => option.value === value) ?? null;
    this.savedValue = selected?.value || '';
    this.onChange(this.savedValue);
    this.cityChange.emit(selected);
  }

  private country(): string { return String(this.countryIso || this.countryId || '').trim().toUpperCase(); }
  private state(): string { return String(this.stateIso || this.stateId || '').trim().toUpperCase(); }
  private contextReady(): boolean { return !!this.country() && !!this.state(); }
  private syncDisabled(): void {
    this.disabled || !this.contextReady()
      ? this.control.disable({ emitEvent: false })
      : this.control.enable({ emitEvent: false });
  }
}

function mergeOptions(current: GeographyOption[], incoming: GeographyOption[]): GeographyOption[] {
  return [...new Map([...current, ...incoming].map((option) => [option.value, option])).values()];
}
